import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { iif, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { LegacyProgressService } from '../../certificates/legacy-progress-service/legacy-progress.service';
import { ShareCertificateIframeModule } from '../../components/share-certificate-iframe/share-certificate-iframe.component';
import { ReferralPageBannerService } from '../../home/referral-page-banner/referral-page-banner.service';
import { SafeUrlPipeModule } from '../../pipes/safe-url.pipe';
import { ProgressPageEventType } from '../../progress/progress-page-event-type';
import { isIFrameSameOrigin } from '../../shared/helpers/is-same-origin';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { OeAppService } from '../../shared/oe-app.service';
import { SensitiveSessionService } from '../../shared/sensitive-session.service';
import { SharedModule } from '../../shared/shared.module';
import { IFrameSizeEventType } from '../../shared/types/iframe-event-type';
import { UserwaySyncModule } from '../../shared/userway-sync.directive';
import { ForceLogOutModalComponent } from './force-log-out-modal/force-log-out-modal.component';

export const enum LevelExamEventType {
  LEVEL_EXAM_RESULT = 'LEVEL_EXAM_RESULT',
  EXAM_PASSED = 'ExamPassed',
}

@Component({
  selector: 'lp2-end-of-level-exam',
  template: `
    <div class="d-flex flex-column">
      <iframe
        *ngIf="levelExamUrl"
        lp2UserwaySync
        class="end-of-level-iframe"
        [src]="levelExamUrl | safeUrl"
        [style.height.px]="height"
        frameborder="0"
      ></iframe>
      <div *ngIf="examPassed && certificateLevel">
        <lp2-share-certificate-iframe [certificateLevel]="certificateLevel"></lp2-share-certificate-iframe>
      </div>
      <div *ngIf="testSubmitted">
        <div *ngIf="referralProgramEligible$ | async" class="referral-banner my-3">
          <lp2-referral-page-banner [displayCloseButton]="false"></lp2-referral-page-banner>
        </div>
        <div class="btn-container d-flex justify-content-center align-items-center">
          <div class="blue-divider flex-1"></div>
          <a class="btn" href="/">{{ 'common_level_exam_results.return_to_study' | translate }} »</a>
          <div class="blue-divider flex-1"></div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./end-of-level-exam.component.scss'],
})
export class EndOfLevelExamComponent implements OnInit, OnDestroy {
  @Input() skipIsExamAvailable: boolean;
  lp2LearnosityUiHost: string;
  height: number;
  levelExamUrl: string;
  examPassed = false;
  testSubmitted = false;
  referralProgramEligible$ = this.referralBannerService.isElligible$;
  certificateLevel: number;

  private readonly destroy$ = new Subject<void>();

  constructor(
    element: ElementRef,
    private oeApp: OeAppService,
    private referralBannerService: ReferralPageBannerService,
    public legacyProgressService: LegacyProgressService,
    private oeAppPersonService: OeAppPersonService,
    private sensitiveSessionService: SensitiveSessionService,
    private modalService: NgbModal,
  ) {
    this.skipIsExamAvailable = element.nativeElement.getAttribute('skip-is-exam-available');
    this.handleEvents = this.handleEvents.bind(this);
  }

  ngOnInit(): void {
    this.lp2LearnosityUiHost = this.oeApp.getData('lp2LearnosityUiHost');
    this.certificateLevel = this.oeAppPersonService.levelNum;

    this.sensitiveSessionService
      .shouldForceLogOut()
      .pipe(takeUntil(this.destroy$))
      .subscribe((forceLogOut) => {
        if (forceLogOut) {
          this.modalService.open(ForceLogOutModalComponent, {
            backdrop: 'static',
            keyboard: false,
          });
          return;
        }
        this.loadLevelExam();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('message', this.handleEvents);
  }

  handleEvents({ data, origin }) {
    if (
      !isIFrameSameOrigin(this.lp2LearnosityUiHost, origin) &&
      !isIFrameSameOrigin(this.oeApp.webProgressPageHost, origin)
    ) {
      return;
    }
    const type: ProgressPageEventType | IFrameSizeEventType | LevelExamEventType = data.type;
    switch (type) {
      case IFrameSizeEventType.HEIGHT:
        if (data.height) {
          this.height = data.height + 20;
        }
        break;
      case LevelExamEventType.EXAM_PASSED:
        this.examPassed = data.examPassed;
        break;
      case LevelExamEventType.LEVEL_EXAM_RESULT:
        this.testSubmitted = true;
        break;
      case ProgressPageEventType.DOWNLOAD_CERTIFICATE:
        this.downloadCertificate(data);
        break;
    }
  }

  private downloadCertificate(data) {
    this.legacyProgressService.downloadCertificatePdf(data.certificateLevel);
  }

  private loadLevelExam(): void {
    this.referralBannerService.isElligible$.pipe(
      switchMap((isElligible) =>
        iif(
          () => isElligible,
          this.referralBannerService.localizedBonusAmount$.pipe(
            map(res => ([isElligible, res]))
          ),
          of([isElligible]))
      ),
      takeUntil(this.destroy$),
    ).subscribe(([isElligible, referralBonusAmount]: [boolean, string]) => {
      let url =
        this.lp2LearnosityUiHost +
        '/level-exam' +
        '?lang=' +
        this.oeAppPersonService.preferredLang +
        '&isReferralEligible=' +
        isElligible +
        '&levelId=' +
        this.oeAppPersonService.levelId +
        '&levelNum=' +
        this.oeAppPersonService.levelNum;

      if (referralBonusAmount) {
        url += '&referralBonusAmount=' + encodeURI(referralBonusAmount);
      }

      if (this.skipIsExamAvailable) {
        url += '&skipIsExamAvailable=' + this.skipIsExamAvailable;
      }

      this.levelExamUrl = url;

      window.addEventListener('message', this.handleEvents);
    });
  }
}

@NgModule({
  entryComponents: [EndOfLevelExamComponent, ForceLogOutModalComponent],
  imports: [CommonModule, SharedModule, SafeUrlPipeModule, ShareCertificateIframeModule, UserwaySyncModule],
  declarations: [EndOfLevelExamComponent, ForceLogOutModalComponent],
})
export class EndOfLevelExamModule {}



// WEBPACK FOOTER //
// ./ts/app/exams/end-of-level-exam/end-of-level-exam.component.ts